import { Suspense, lazy, useEffect, useMemo } from "react";
import { isExpired } from "react-jwt";
import { Outlet, useLocation } from "react-router-dom";
import cookie from "react-cookies";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { Box } from "@mui/material";
import { apiGetDomains } from "../api/systemLibrary";
import { useGlobalData } from "../hooks/useGlobalData";
import { getURLDomain, logoutFunction } from "../common/utils";
import { RIMS_COOKIE_CONSTANTS } from "../constants";
import useDomainStore from "../store/domain";
import { useConfigStore } from "../store/config";
import RouteFallback from "../component/RouteFallback";
import "../assets/scss/page/index.scss";
import { useUrfReceiveEvents } from "../hooks/urfEvents";
import { useQuery } from "@tanstack/react-query";
import { apiGetFeatureFlagListAll } from "../api/pm";
import { useFeatureFlagStore } from "../store/featureFlagStore";
import { getMuiLocaleBundles } from "../utils/muiLocales";

const FreyrLibraryProvider = lazy(() =>
  import("@freyr/freyr-react-common").then((m) => ({ default: m.FreyrLibraryProvider }))
);

const Layouts = () => {
  const { token, tenant, domain: cookieDomain } = useGlobalData();
  const setFlags = useFeatureFlagStore((s) => s.setFlags);
  const { language } = useConfigStore();
  const URLDomain = getURLDomain();
  const redirectURL = `https://portal.${URLDomain}`;
  const { updateDomain, domainValue } = useDomainStore();
  const location = useLocation();
  const isBpmnRoute = location.pathname.startsWith("/bpmn");
  const isAuthenticated = Boolean(token && !isExpired(token));

  const themeWithLocale = useMemo(() => {
    const { core, grid, pickers } = getMuiLocaleBundles(language);
    return createTheme({ palette: {} }, core, grid, pickers);
  }, [language]);

  useUrfReceiveEvents();

  useEffect(() => {
    if (!isAuthenticated) {
      logoutFunction();
      window.location.href = `${redirectURL}?redirect=${window.location.origin}`;
    }
  }, [isAuthenticated, redirectURL]);

  useEffect(() => {
    if (!cookieDomain) {
      updateDomain({
        domainName: "ALL",
        domainId: "-1",
      });
      return;
    }
    apiGetDomains()
      .then((res) => {
        const result = res?.data;
        if (Array.isArray(result) && result.length > 0) {
          const curDomain = result.find((o) => o.domainPhrase === cookieDomain);
          if (curDomain) {
            updateDomain({
              domainName: curDomain?.domainPhrase,
              domainId: curDomain?.value,
            });
          }
        }
      })
      .catch(() => {});
  }, [cookieDomain, updateDomain]);

  const { data: featureFlagList } = useQuery(
    ["fetchFeatureFlagListAll"],
    () => apiGetFeatureFlagListAll(),
    {
      enabled: isAuthenticated,
      retry: false,
      refetchOnWindowFocus: false,
      select: (response) => response?.data || {},
    }
  );

  useEffect(() => {
    if (featureFlagList) {
      setFlags(featureFlagList);
    }
  }, [featureFlagList, setFlags]);

  if (!isAuthenticated) {
    return <RouteFallback />;
  }

  return (
    <ThemeProvider theme={themeWithLocale}>
      <Suspense fallback={<RouteFallback />}>
        <FreyrLibraryProvider
          token={token}
          language={language}
          tenant={
            isBpmnRoute
              ? cookie.load(RIMS_COOKIE_CONSTANTS.AC_TENANT_ID) || tenant
              : tenant
          }
          domain={cookieDomain ? domainValue.domainId : undefined}
          domainName={cookieDomain ? domainValue.domainName : undefined}
        >
          <Box className="custom-page" data-testid="custom-page">
            <Suspense fallback={<RouteFallback />}>
              <Outlet />
            </Suspense>
          </Box>
        </FreyrLibraryProvider>
      </Suspense>
    </ThemeProvider>
  );
};

export default Layouts;
